import React, { useState, useEffect } from 'react';
import { database } from '../supabase';
import './Hero.css';
import FeaturedCarousel from './FeaturedCarousel';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: string;
  images: string[];
  size: string;
  category?: string;
  stock_quantity: number;
  is_active: boolean;
}

interface Post {
  id: string;
  type: 'product' | 'event' | 'banner' | 'content';
  title?: string;
  description?: string;
  image: string;
  product_id?: string;
  aspect_ratio?: 'square' | 'portrait' | 'landscape' | 'wide';
  position?: number;
  created_at: string;
  is_active: boolean;
}

interface HeroProps {
  onShopNow: () => void;
}

const Hero: React.FC<HeroProps> = ({ onShopNow }) => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch featured products and posts from database
  useEffect(() => {
    const fetchFeed = async () => {
      try {
        // Load products
        const { data: productsData, error: productsError } = await database.getProducts();
        if (productsError) {
          console.error('Error fetching featured products:', productsError);
          return;
        }

        // Load posts (gracefully handle if table doesn't exist yet)
        let postsData = null;
        try {
          const result = await database.getPosts();
          postsData = result.data;
          if (result.error) {
            console.warn('Error loading posts (continuing without posts):', result.error);
          }
        } catch (err) {
          console.warn('Posts table may not exist yet (continuing without posts):', err);
        }

        if (productsData) {
          // Transform products
          const transformedProducts = productsData.map((product: any) => ({
            id: product.id,
            name: product.name,
            description: product.description,
            price: `$${product.price.toFixed(2)}`,
            images: product.images || ['/images/placeholder.jpg'],
            size: product.size || 'SIZE: ONE SIZE',
            category: product.category,
            stock_quantity: product.stock_quantity,
            is_active: product.is_active
          }));
          setFeaturedProducts(transformedProducts);
        }

        // Transform posts
        if (postsData) {
          const transformedPosts = postsData.map((post: any) => ({
            id: post.id,
            type: post.type || 'content',
            title: post.title,
            description: post.description,
            image: post.image,
            product_id: post.product_id,
            aspect_ratio: post.aspect_ratio || 'square',
            created_at: post.created_at,
            is_active: post.is_active
          }));
          setPosts(transformedPosts);
        }
      } catch (error) {
        console.error('Error loading feed:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeed();
  }, []);

  if (loading) {
    return (
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">ANTI-STYLE HYPOTHESIS</h1>
          <p className="hero-subtitle">CURATED VINTAGE CLOTHING FROM 1950-2000</p>
          <div className="hero-buttons">
            <button className="shop-now-btn" onClick={onShopNow}>SHOP NOW</button>
            <button className="view-all-btn">VIEW ALL</button>
          </div>
        </div>
        <div className="loading-showcase">
          <p>Loading featured products...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="hero">
      <div className="hero-content">
        <h1 className="hero-title">ANTI-STYLE HYPOTHESIS</h1>
        <p className="hero-subtitle">CURATED VINTAGE CLOTHING FROM 1950-2000</p>
        <div className="hero-buttons">
          <button className="shop-now-btn" onClick={onShopNow}>SHOP NOW</button>
          <button className="view-all-btn" onClick={onShopNow}>VIEW ALL</button>
        </div>
      </div>

      <FeaturedCarousel items={featuredProducts.slice(0, 8)} />

      <div className="instagram-feed">
        {/* Group products in pattern: 6 small, 3 large, with posts inserted based on position */}
        {(() => {
          const feedItems: React.ReactElement[] = [];
          let productIndex = 0;
          let groupIndex = 0; // Track which group we're on (0 = first, 1 = second, etc.)
          
          // Filter active posts and sort by position
          const activePosts = posts
            .filter(post => post.is_active)
            .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
          
          // Process products in groups of 9 (6 small + 3 large)
          while (productIndex < featuredProducts.length) {
            // Get next 6 products for small grid
            const smallProducts = featuredProducts.slice(productIndex, productIndex + 6);
            productIndex += 6;
            
            // Get next 3 products for large grid
            const largeProducts = featuredProducts.slice(productIndex, productIndex + 3);
            productIndex += 3;
            
            // Add small products (6 items in 3x2 grid)
            if (smallProducts.length > 0) {
              feedItems.push(
                <div key={`small-group-${groupIndex}`} className="small-products-group">
                  {smallProducts.map((product) => (
                    <div key={`product-${product.id}`} className="feed-item product-item small-item" onClick={() => onShopNow()} role="button" tabIndex={0}>
                      <div className="feed-image-container">
                        <img
                          src={product.images[0] || '/images/placeholder.jpg'}
                          alt={product.name}
                          className="feed-image"
                          loading="lazy"
                        />
                      </div>
                      <div className="feed-caption">
                        <span className="product-name-short">
                          {product.name.length > 30 ? `${product.name.substring(0, 30)}...` : product.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }
            
            // Add large products (3 items horizontally)
            if (largeProducts.length > 0) {
              feedItems.push(
                <div key={`large-group-${groupIndex}`} className="large-products-group">
                  {largeProducts.map((product) => (
                    <div key={`product-${product.id}`} className="feed-item product-item large-item" onClick={() => onShopNow()} role="button" tabIndex={0}>
                      <div className="feed-image-container">
                        <img
                          src={product.images[0] || '/images/placeholder.jpg'}
                          alt={product.name}
                          className="feed-image"
                          loading="lazy"
                        />
                      </div>
                      <div className="feed-caption">
                        <span className="product-name-short">
                          {product.name.length > 30 ? `${product.name.substring(0, 30)}...` : product.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }
            
            // Check if there's a post that should appear after this group
            const postForThisPosition = activePosts.find(post => post.position === groupIndex);
            if (postForThisPosition) {
              feedItems.push(
                <div 
                  key={`post-${postForThisPosition.id}`} 
                  className={`feed-item post-item post-${postForThisPosition.aspect_ratio || 'square'} post-${postForThisPosition.type}`}
                >
                  <div className="feed-image-container">
                    <img
                      src={postForThisPosition.image}
                      alt={postForThisPosition.title || 'Post'}
                      className="feed-image"
                      loading="lazy"
                    />
                  </div>
                  {(postForThisPosition.title || postForThisPosition.description) && (
                    <div className="feed-caption post-caption">
                      {postForThisPosition.title && (
                        <span className="post-title-short">
                          {postForThisPosition.title.length > 30 ? `${postForThisPosition.title.substring(0, 30)}...` : postForThisPosition.title}
                        </span>
                      )}
                      {postForThisPosition.description && (
                        <span className="post-description-short">
                          {postForThisPosition.description.length > 50 ? `${postForThisPosition.description.substring(0, 50)}...` : postForThisPosition.description}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            
            groupIndex++;
          }
          
          return feedItems;
        })()}
      </div>
    </section>
  );
};

export default Hero;
